import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ObjectCardInfoItem from './ObjectCardInfoItem';
import SvgDollar from 'components/svg/SvgDollar';
import SvgPackage from 'components/svg/SvgPackage';
import SvgUsers from 'components/svg/SvgUsers';
import SvgShuffle from 'components/svg/SvgShuffle';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    getBaseCurrencyCode,
    getBaseCurrencyPrefix,
    getCurrencyCode,
    withBaseCurrency,
    withLocalCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import { useObjectCard } from '../context/ObjectCardProvider';
import vconst from 'utils/helpers/structs/vconst';
import { useEffect, useMemo, useState } from 'react';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import * as apiObject from 'api/apiObjects';
import ObjectPayStatus from '../../ObjectsList/ObjectPayStatus/ObjectPayStatus';
import {
    getObjectPaymentsCoverageRatio,
    isObjectPaymentsCovered,
    getObjectDebtAmount,
} from '../../ObjectsList/helpers/objectPayCoverage';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import OffHistoryPrice from '../OffHistoryPrice';
import { getActualObjectCost } from '../OffHistoryPrice/model';
import SolarIcon from 'components/icons/SolarIcon';

const ObjectcardInfo = ({ priceSummary, setPriceSummary }) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const { objectId, vObject, vObjectInfo, setVObject, setShowSpinner, isEditLocked } = useObjectCard();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const showPayments = Boolean(params.objectPaymentControl);
    const canManualPayStatus = Boolean(
        params.objectPaymentControl && params.manualPayStatus,
    );
    const [payStatusBusy, setPayStatusBusy] = useState(false);
    const [showHistoryPrice, setShowHistoryPrice] = useState(false);
    const canChangePrice =
        hasUserAccess(auth?.user, 'objectCreate') && !isEditLocked;
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const object = vObject?.object;
    const priceObject = object?.objectPrice;
    const priceObjectRate = object?.objectPriceRate;
    const expendTeams = vObject.expendTeams;
    const expends = vObject.expends;

    const priceMaterials = vObjectInfo?.prodPrice || 0;
    const priceMaterialsRate = vObjectInfo?.prodPriceRate || 0;
    const priceMaterialsSale = (vObjectInfo?.prodPriceSale || 0) - priceMaterials;
    const priceMaterialsSaleRate = (vObjectInfo?.prodPriceSaleRate || 0) - priceMaterialsRate;

    let expendTeamAmount = 0;
    let expendTeamAmountRate = 0;
    let expendTeamSale = 0;
    let expendTeamSaleRate = 0;
    expendTeams?.map((expend) => {
        if (expend.income_status == vconst.incomeStatActive) {
            const count = Number(expend.income_count ?? expend.incomeCount ?? 1) || 1;
            const priceSale = Number(expend.income_price_sale ?? expend.incomePriceSale ?? 0);
            const priceSaleRate = Number(expend.income_price_sale_rate ?? expend.incomePriceSaleRate ?? 0);
            expendTeamAmount = expendTeamAmount + expend.income_amount;
            expendTeamAmountRate = expendTeamAmountRate + expend.income_amount_rate;
            expendTeamSale = expendTeamSale + count * priceSale;
            expendTeamSaleRate = expendTeamSaleRate + count * priceSaleRate;
        }
    });
    const priceServicesSale = expendTeamSale - expendTeamAmount;
    const priceServicesSaleRate = expendTeamSaleRate - expendTeamAmountRate;

    let expendAmount = 0;
    let expendAmountRate = 0;
    expends?.map((expend) => {
        const income = expend.expend_income ? 1 : -1;
        if (expend.expendStatus == vconst.incomeStatActive) {
            expendAmount = expendAmount + income * expend.expendAmount;
            expendAmountRate = expendAmountRate + income * expend.expendAmountRate;
        }
    });

    useEffect(() => {
        setPriceSummary(priceObject - priceMaterials - expendTeamAmount - expendAmount);
    }, [vObjectInfo, vObject, priceObject, priceMaterials, expendTeamAmount, expendAmount]);

    const actualCost = useMemo(
        () => getActualObjectCost(vObject, vObjectInfo, params.includeOtherInObjectCost),
        [vObject, vObjectInfo, params.includeOtherInObjectCost],
    );

    const paidUsd = Number(object?.paidUsd || 0);
    const paidUah = Number(object?.paidUah || 0);
    const hasPayments = paidUsd !== 0 || paidUah !== 0;
    const coverageParams = {
        objectPrice: priceObject,
        objectPriceRate: priceObjectRate,
        paidUsd,
        paidUah,
        rate,
    };
    const paymentsCovered = isObjectPaymentsCovered(coverageParams);
    const coverageRatio = getObjectPaymentsCoverageRatio(coverageParams);
    const coveragePercent = Math.min(100, Math.round(Math.max(0, coverageRatio) * 100));
    const debtAmount = getObjectDebtAmount(coverageParams);
    const paymentsTone = !hasPayments ? 'empty' : paymentsCovered ? 'covered' : 'partial';
    const paymentsStatusLabel = paymentsCovered
        ? t('infoItems.objectPaymentsCovered')
        : t('infoItems.objectPaymentsPartial');
    const paymentsStatusIcon = paymentsCovered
        ? 'bi-check-circle-fill'
        : 'bi-hourglass-split';

    const handlePayStatusChange = async (payStatus) => {
        if (!objectId || payStatusBusy) return;
        setPayStatusBusy(true);
        setShowSpinner?.(true);
        try {
            const res = await apiObject.setPayStatus(objectId, payStatus);
            if (res) {
                setVObject((prev) => {
                    if (!prev?.object) return prev;
                    return {
                        ...prev,
                        object: {
                            ...prev.object,
                            objectPayStatus: payStatus,
                        },
                    };
                });
                showToast(t('messPayStatusChanged'), 'success');
            } else {
                showToast(t('common:messError'), 'danger');
            }
        } finally {
            setPayStatusBusy(false);
            setShowSpinner?.(false);
        }
    };

    const handlePriceChanged = async (nextPrice, nextRate) => {
        setVObject((prev) => {
            if (!prev?.object) return prev;
            return {
                ...prev,
                object: {
                    ...prev.object,
                    objectPrice: nextPrice,
                    objectPriceRate: nextRate,
                },
            };
        });
    };

    return (
        <>
        <OffHistoryPrice
            show={showHistoryPrice}
            setShow={setShowHistoryPrice}
            objectId={Number(objectId)}
            objectPrice={Number(priceObject) || 0}
            objectPriceRate={Number(priceObjectRate) || 0}
            onSuccess={handlePriceChanged}
        />
        <Card className="object-analytics-card">
            <Card.Body className="object-analytics-card__body p-0">
                <div className="object-analytics-card__header">
                    <h5 className="object-analytics-card__title">
                        <SolarIcon name="graph-up-arrow" className="object-analytics-card__title-icon" aria-hidden="true" />
                        {t('titleAnalytics')}
                    </h5>
                </div>
 
                <div className="object-analytics-card__content">
                    <ul className="object-analytics-list list-unstyled mb-0">
                        <ObjectCardInfoItem
                            isUahMode={isUahMode}
                            title={t('infoItems.objectPrice')}
                            icon={<SvgDollar width={18} />}
                            amount={priceObject}
                            actionLabel={canChangePrice ? t('common:buttons.change') : undefined}
                            onAction={canChangePrice ? () => setShowHistoryPrice(true) : undefined}
                            noteLabel={t('infoItems.objectActualCost')}
                            noteAmount={actualCost}
                        />

                        <ObjectCardInfoItem
                            isUahMode={isUahMode}
                            title={`${t('infoItems.objectMaterials')} (${t('infoItems.objectIncome')})`}
                            icon={<SvgPackage width={18} />}
                            amountTitle={priceMaterialsRate}
                            amount={-priceMaterials}
                            secondAmount={priceMaterialsSale}
                            secondAmountTitle={priceMaterialsSaleRate}
                        />

                        <ObjectCardInfoItem
                            isUahMode={isUahMode}
                            title={`${t('infoItems.objectServices')} (${t('infoItems.objectIncome')})`}
                            icon={<SvgUsers width={18} />}
                            amount={-expendTeamAmount}
                            amountTitle={expendTeamAmountRate}
                            secondAmount={priceServicesSale}
                            secondAmountTitle={priceServicesSaleRate}
                        />

                        <ObjectCardInfoItem
                            isUahMode={isUahMode}
                            title={t('infoItems.objectOther')}
                            icon={<SvgShuffle width={18} />}
                            amount={-expendAmount}
                            amountTitle={expendAmountRate}
                        />
                    </ul>
                </div>

                {showPayments ? (
                <div className={`object-analytics-payments object-analytics-payments--${paymentsTone}`}>
                    <div className="object-analytics-payments__glow" aria-hidden="true" />
                    <div className="object-analytics-payments__head">
                        <span className="object-analytics-payments__icon" aria-hidden="true">
                            <SolarIcon name="wallet2" />
                        </span>
                        <div className="object-analytics-payments__titles">
                            <div className="object-analytics-payments__title-row">
                                <div className="object-analytics-payments__title-stack">
                                    <span className="object-analytics-payments__label">
                                        {t('infoItems.objectPayments')}
                                    </span>
                                    <span className="object-analytics-payments__hint">
                                        {t('infoItems.objectPaymentsHint')}
                                    </span>
                                </div>
                                {hasPayments ? (
                                    <span className="object-analytics-payments__status">
                                        <SolarIcon name={paymentsStatusIcon} aria-hidden="true" />
                                        {paymentsStatusLabel}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="object-analytics-payments__chips">
                        {hasPayments ? (
                            <>
                                {paidUsd !== 0 ? (
                                    <div className="object-analytics-payments__chip">
                                        <span className="object-analytics-payments__chip-cur">{getBaseCurrencyCode()}</span>
                                        <span className="object-analytics-payments__chip-val">
                                            {withBaseCurrency(formatPrice(paidUsd))}
                                        </span>
                                    </div>
                                ) : null}
                                {paidUah !== 0 ? (
                                    <div className="object-analytics-payments__chip">
                                        <span className="object-analytics-payments__chip-cur">{getCurrencyCode()}</span>
                                        <span className="object-analytics-payments__chip-val">
                                            {withLocalCurrency(formatPrice(paidUah))}
                                        </span>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="object-analytics-payments__chip object-analytics-payments__chip--empty">
                                <span className="object-analytics-payments__chip-val">—</span>
                            </div>
                        )}
                    </div>

                    <div className="object-analytics-payments__progress">
                        <div className="object-analytics-payments__progress-meta">
                            <span>{t('infoItems.objectPaymentsCoverage')}</span>
                            <span className="object-analytics-payments__progress-pct">
                                {coveragePercent}%
                            </span>
                        </div>
                        <div
                            className="object-analytics-payments__track"
                            role="progressbar"
                            aria-valuenow={coveragePercent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <div
                                className="object-analytics-payments__fill"
                                style={{ width: `${coveragePercent}%` }}
                            />
                        </div>
                    </div>
                    {debtAmount > 0 ? (
                        <div className="d-flex justify-content-between align-items-center mt-2 fs-12">
                            <span className="text-danger fw-medium">{t('labelDebt')}</span>
                            <span className="text-danger fw-medium">
                                {isUahMode
                                    ? withLocalCurrency(formatPrice(debtAmount))
                                    : withBaseCurrency(formatPrice(debtAmount))}
                            </span>
                        </div>
                    ) : null}
                </div>
                ) : null}

                {canManualPayStatus ? (
                    <div className="object-analytics-pay-status">
                        <span className="object-analytics-pay-status__label">
                            {t('fields.payStatus')}
                        </span>
                        <ObjectPayStatus
                            objectId={Number(objectId)}
                            payStatus={object?.objectPayStatus}
                            objectPrice={priceObject}
                            objectPriceRate={priceObjectRate}
                            paidUsd={paidUsd}
                            paidUah={paidUah}
                            rate={rate}
                            editable
                            showCoveredHint={false}
                            disabled={payStatusBusy}
                            onChange={handlePayStatusChange}
                        />
                    </div>
                ) : null}

                <div className="object-analytics-card__footer">
                    <span className="object-analytics-card__footer-label">
                        {t('infoItems.objectProfitability')}
                    </span>
                    <h4 className={`object-analytics-card__footer-value mb-0${priceSummary < 0 ? ' text-danger' : ''}`}>
                        {isUahMode ? (
                            <>{withLocalCurrency(formatPrice(priceSummary))}</>
                        ) : (
                            <>
                                <small className={priceSummary < 0 ? 'text-danger' : 'text-muted'}>{getBaseCurrencyPrefix()}</small>
                                {formatPrice(priceSummary)}
                            </>
                        )}
                    </h4>
                </div>
            </Card.Body>
        </Card>
        </>
    );
};

export default ObjectcardInfo;
